// Practice quiz banks. Each question has a stable id for progress tracking.
export const QUIZZES = {
  qzA: {
    label: "Data prep",
    questions: [
      {
        id: "qzA-1",
        q: "A retail dataset has a discount_percent column that is blank on every day when no promotion was running. An ML engineer must prepare the column for training with the least distortion of the data. What should the engineer do?",
        o: ["Fill the blanks with the column mean", "Fill the blanks with zero", "Drop every row that has a blank value", "Drop the column entirely"],
        a: 1,
        e: "Blank here MEANS \"no discount\", a real value of 0. Filling with the mean invents promotions that never happened; dropping rows or the column throws away good data. Only impute statistically when values are missing at random.",
      },
      {
        id: "qzA-2",
        q: "A company has ten years of house sales sorted by sale date. The model must predict prices for future sales. Which train/test split should the engineer use?",
        o: ["Randomized split", "Ordered split", "Split by key", "Stratified split on ZIP code"],
        a: 1,
        e: "Time-ordered data must train on the past and test on the future. A random shuffle puts later sales into training and earlier sales into testing, so the model peeks at the future and the test score lies.",
      },
      {
        id: "qzA-3",
        q: "A categorical column contains about 4,000 distinct city values that were typed by hand, so many differ only by spelling errors. Which encoding prepares this column for training?",
        o: ["One-hot encoding", "Label encoding", "Similarity encoding", "Binary encoding"],
        a: 2,
        e: "Similarity encoding groups near-identical strings into shared representations, collapsing the typo explosion. One-hot would create thousands of near-duplicate columns.",
      },
    ],
  },
  qzB: {
    label: "Training",
    questions: [
      {
        id: "qzB-1",
        q: "A team trains models with custom PyTorch scripts. The scripts import an internal library that is published only to the company private artifact repository. The team needs to run the training on SageMaker with the least operational overhead. What should the team do?",
        o: ["Use a SageMaker built-in algorithm", "Use script mode with the prebuilt PyTorch container as is", "Extend the prebuilt PyTorch container to include the internal library", "Build a container from scratch and push it to Amazon ECR"],
        a: 2,
        e: "Ordinary libraries can be installed at job start with requirements.txt in script mode, but a PRIVATE repository is the signal that pip cannot simply fetch it, so bake it in once by extending the prebuilt image. Building from scratch discards a container that is already 90 percent right.",
      },
      {
        id: "qzB-2",
        q: "An engineer must tune a large deep learning model and wants to minimize total computation time across the tuning job. Which strategy should the engineer choose?",
        o: ["Grid search", "Random search", "Bayesian optimization", "Hyperband"],
        a: 3,
        e: "Hyperband starts many trials and aggressively stops the underperformers early, so the least compute is spent overall. Bayesian minimizes the NUMBER of trials but runs each one to completion.",
      },
      {
        id: "qzB-3",
        q: "A training job for a large neural network fails partway through with a GPU out-of-memory error. Which change most directly resolves the failure?",
        o: ["Reduce the batch size", "Decrease the learning rate", "Increase the number of epochs", "Add L2 regularization"],
        a: 0,
        e: "Batch size controls how much data sits on the GPU per step, so it is the memory dial. Learning rate and regularization change how the model learns, not how much memory it consumes.",
      },
    ],
  },
  qzC: {
    label: "Serving",
    questions: [
      {
        id: "qzC-1",
        q: "A company hosts 12 XGBoost models, one per region, each on its own real-time endpoint. Every endpoint is heavily underutilized and the company wants to reduce hosting cost. Occasional load delays are acceptable. What should the company do?",
        o: ["Configure a multi-model endpoint", "Configure a multi-container endpoint", "Configure an inference pipeline", "Move each endpoint to a smaller instance type"],
        a: 0,
        e: "All 12 use the same framework and cold starts are tolerable, so they can share one endpoint and load from S3 on demand. Multi-container is for DIFFERENT frameworks; smaller instances still pay 12 separate idle bills.",
      },
      {
        id: "qzC-2",
        q: "An application uploads video files of up to 250 MB to Amazon S3. A model must process each uploaded file, which takes about 12 minutes, and the results must be stored for later retrieval. Which deployment meets these requirements?",
        o: ["Real-time endpoint with auto scaling", "Serverless endpoint with provisioned concurrency", "Asynchronous endpoint with a scaling policy", "Batch transform job scheduled hourly"],
        a: 2,
        e: "250 MB exceeds the 6 MB real-time and 4 MB serverless limits, and 12 minutes exceeds their 60-second ceilings; 12 minutes still fits async (60-minute max). Async also queues per-upload requests and writes results to S3.",
      },
      {
        id: "qzC-3",
        q: "An ML engineer must validate a new model version on 5 percent of live traffic on an existing SageMaker endpoint, with the least operational overhead. What should the engineer do?",
        o: ["Create a second endpoint and split traffic with an Application Load Balancer", "Add the new model as a production variant on the existing endpoint and set its weight to 0.05", "Configure shadow testing with a 5 percent sampling percentage", "Configure a blue/green deployment with all-at-once traffic shifting"],
        a: 1,
        e: "Production variants split live traffic by weight on one endpoint — 0.05 equals 5 percent. ALBs cannot target SageMaker endpoints, and shadow testing sends users no traffic at all, so it validates nothing about real user experience.",
      },
    ],
  },
  qzD: {
    label: "Security",
    questions: [
      {
        id: "qzD-1",
        q: "Training data is stored in an Amazon S3 bucket in a different AWS Region from the account where SageMaker training will run. The data must never traverse the public internet. Which solution meets the requirement?",
        o: ["Disable direct internet access and create a gateway VPC endpoint", "Disable direct internet access and create an interface VPC endpoint", "Encrypt the bucket with a customer managed KMS key", "Attach an IAM policy that allows only the training role to read the bucket"],
        a: 1,
        e: "Gateway endpoints reach S3 only within the SAME Region, so a cross-Region bucket needs an interface endpoint (PrivateLink). KMS and IAM are the wrong layers entirely: encryption controls readability and IAM controls permission, and neither controls the network PATH.",
      },
      {
        id: "qzD-2",
        q: "A SageMaker Processing job can list the contents of an S3 bucket but receives 403 errors when it reads the CSV objects inside it. What is the most likely cause?",
        o: ["The SageMaker Studio execution role lacks permissions", "The Processing job execution role lacks object-level permissions", "The bucket does not exist", "The VPC endpoint policy blocks Amazon S3"],
        a: 1,
        e: "Every component calls AWS with its OWN role, and the Processing job is the caller here, not the Studio notebook. Listing works while reading fails, which points precisely at missing object-level permissions such as s3:GetObject.",
      },
      {
        id: "qzD-3",
        q: "A SageMaker domain runs in a public subnet. The security team discovers repeated suspicious requests from one external IP address and wants that address blocked. What should the engineer configure?",
        o: ["A security group inbound rule that denies the IP address", "A network ACL inbound rule that denies the IP address", "A route table entry that drops traffic from the IP address", "An IAM policy that denies the IP address"],
        a: 1,
        e: "Security groups support ALLOW rules only, so they cannot deny a specific address. Subnet-level network ACLs support explicit DENY rules, so blocking a single IP is always a NACL job.",
      },
    ],
  },
  qzE: {
    label: "GenAI & Bedrock",
    questions: [
      {
        id: "qzE-1",
        q: "Every night, an application must send about 500,000 product descriptions to a large language model on Amazon Bedrock and store each generated summary in Amazon S3. Which solution requires the least operational overhead?",
        o: ["Create a Step Functions workflow that calls InvokeModel once per description and writes each result to S3", "Create a Bedrock batch inference job that reads a JSONL file from an S3 location and writes output to an S3 location", "Deploy the model to a SageMaker real-time endpoint and invoke it from an AWS Lambda function per description", "Create an Amazon EventBridge rule that invokes a Lambda function for each description"],
        a: 1,
        e: "Bedrock batch inference is built for exactly this: one CreateModelInvocationJob call with an S3 input location and an S3 output location. Looping half a million individual invocations through Step Functions or Lambda hand-builds what the service already provides.",
      },
      {
        id: "qzE-2",
        q: "Users report that a Bedrock-powered assistant gives noticeably different answers each time they ask the same question. The team wants more consistent responses. What should the team change?",
        o: ["Increase the temperature parameter and increase top_k", "Decrease the temperature parameter and decrease top_k", "Increase the maximum number of generated tokens", "Add a stop sequence to the request"],
        a: 1,
        e: "Temperature and top_k are both randomness dials; lowering both makes generation more deterministic. Token limits and stop sequences control the LENGTH and shape of output, not its variability.",
      },
      {
        id: "qzE-3",
        q: "A company wants a fully managed retrieval augmented generation solution over PDF documents in Amazon S3. The assistant will receive a consistently high volume of queries 24 hours a day. Which combination is most cost-effective?",
        o: ["Amazon OpenSearch Service for retrieval with Bedrock on-demand pricing", "Amazon OpenSearch Service for retrieval with Bedrock provisioned throughput", "Amazon Bedrock Knowledge Bases for retrieval with Bedrock on-demand pricing", "Amazon Bedrock Knowledge Bases for retrieval with Bedrock provisioned throughput"],
        a: 3,
        e: "Two independent choices: \"fully managed RAG\" rules out OpenSearch, where you build the embedding and retrieval plumbing yourself; and constant 24/7 volume makes provisioned throughput cheaper than paying per token on demand.",
      },
    ],
  },
};

export const ALL_QUESTION_IDS = Object.values(QUIZZES).flatMap((q) => q.questions.map((x) => x.id));
export const TOTAL_QUESTIONS = ALL_QUESTION_IDS.length;

// Which chapter each quiz belongs to (for completion badges in the TOC).
export const CHAPTER_QUIZ = { c3: "qzA", c5: "qzB", c6: "qzC", c9: "qzD", c10: "qzE" };

export const RANKS = [
  { name: "Intern", min: 0 },
  { name: "Junior", min: 30 },
  { name: "Associate", min: 70 },
  { name: "Engineer", min: 110 },
  { name: "Cloud Architect", min: 150 },
];

export const PRAISE = ["Nice.", "Sharp.", "Exactly.", "Locked in.", "You got it.", "Clean kill.", "Textbook."];
